/**
 * Patch anatomy - travelling callouts for the exploded four-layer render.
 *
 * The markup ships every layer as a readable card and the four controls as a
 * plain list of buttons. The inline script in the section has already added
 * `pp-anatomy--enhanced`, hidden layers 2-4 and placed the resting marker by
 * the time this runs, so what is left is the tab semantics and the animation:
 * a hairline travels in from the side the layer is anchored to, then hands over
 * to the numbered marker and its caption.
 *
 * Tab roles are applied here rather than in Liquid on purpose. Without
 * JavaScript there is no tablist - the cards are just a list - and announcing
 * one would promise an interaction that cannot happen.
 *
 * The marker layer is aria-hidden in the markup, so none of the animation is
 * announced. It mirrors a button that already carries the name and the state.
 */
class PerfectPatchesPatchAnatomy extends HTMLElement {
  connectedCallback() {
    this.hotspots = [...this.querySelectorAll('[data-pp-hotspot]')];
    this.layers = [...this.querySelectorAll('[data-pp-layer]')];
    this.pins = [...this.querySelectorAll('[data-pp-pin]')];
    this.trace = this.querySelector('[data-pp-trace]');
    this.list = this.querySelector('[data-pp-hotspots]');
    this.media = this.querySelector('[data-pp-media]');

    if (this.hotspots.length === 0 || this.hotspots.length !== this.layers.length) return;

    this.classList.add('pp-anatomy--enhanced');

    if (this.list) {
      this.list.setAttribute('role', 'tablist');
      this.list.setAttribute('aria-orientation', 'vertical');
    }

    this.hotspots.forEach((hotspot, index) => {
      const layer = this.layers[index];
      hotspot.setAttribute('role', 'tab');
      hotspot.setAttribute('aria-controls', layer.id);
      layer.setAttribute('role', 'tabpanel');
      layer.setAttribute('aria-labelledby', hotspot.id);
      layer.setAttribute('tabindex', '0');
    });

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);

    const active = this.hotspots.findIndex((hotspot) => hotspot.classList.contains('is-active'));
    this.select(active === -1 ? 0 : active, { moveFocus: false, animate: false });
    this.watchForEntry();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    this.settle();
  }

  handleClick = (event) => {
    const hotspot = event.target.closest('[data-pp-hotspot]');
    if (!hotspot || !this.contains(hotspot)) return;
    this.settle();
    this.select(this.hotspots.indexOf(hotspot), { moveFocus: false });
  };

  handleKeydown = (event) => {
    const hotspot = event.target.closest('[data-pp-hotspot]');
    if (!hotspot || !this.contains(hotspot)) return;

    const current = this.hotspots.indexOf(hotspot);
    const last = this.hotspots.length - 1;
    let next = null;

    // Both axes are bound: the controls read as a column beside the render and
    // as a stack beneath it on a phone, so either arrow pair is a fair guess.
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = current === last ? 0 : current + 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = current === 0 ? last : current - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;

    if (next === null) return;
    event.preventDefault();
    this.settle();
    this.select(next, { moveFocus: true });
  };

  /**
   * The section demonstrates itself. Layer one is already on the render at
   * rest; when the artwork comes into view its arrival is replayed once, so the
   * customer sees what pressing a control does before being asked to press one.
   * The nudge down the remaining buttons then says where to press.
   */
  watchForEntry() {
    if (!this.media || typeof IntersectionObserver !== 'function') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.observer = null;
      this.animateTo(this.active);
      this.hintTimer = window.setTimeout(() => {
        if (this.list) this.list.classList.add('is-hinting');
        this.hintTimer = window.setTimeout(() => {
          if (this.list) this.list.classList.remove('is-hinting');
          this.hintTimer = null;
        }, 1000);
      }, 900);
    }, { threshold: 0.45 });

    this.observer.observe(this.media);
  }

  /** Anything the customer does outranks the invitation to do something. */
  settle() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.hintTimer) {
      window.clearTimeout(this.hintTimer);
      this.hintTimer = null;
    }
    if (this.list) this.list.classList.remove('is-hinting');
  }

  animateTo(index) {
    const pin = this.pins[index];
    if (!pin) return;

    if (this.trace) {
      const side = pin.dataset.ppSide === 'left' ? 'left' : 'right';
      this.trace.className = `pp-anatomy__trace pp-anatomy__trace--${side}`;
      this.trace.style.setProperty('--pp-trace-x', pin.style.getPropertyValue('--pp-x'));
      this.trace.style.setProperty('--pp-trace-y', pin.style.getPropertyValue('--pp-y'));
      void this.trace.offsetWidth;
      this.trace.classList.add('is-running');
    }

    // Restarting a running animation needs the class gone and the layout
    // flushed, or a fast second press is ignored.
    pin.classList.remove('is-arriving');
    void pin.offsetWidth;
    pin.classList.add('is-arriving');
  }

  select(index, { moveFocus, animate = true }) {
    this.active = index;

    this.hotspots.forEach((hotspot, position) => {
      const isActive = position === index;
      hotspot.classList.toggle('is-active', isActive);
      hotspot.setAttribute('aria-selected', String(isActive));
      hotspot.tabIndex = isActive ? 0 : -1;
      this.layers[position].hidden = !isActive;

      const pin = this.pins[position];
      if (!pin) return;
      pin.classList.toggle('is-shown', isActive);
      if (!isActive) pin.classList.remove('is-arriving');
    });

    if (animate) this.animateTo(index);
    if (moveFocus) this.hotspots[index].focus();
  }
}

if (!customElements.get('pp-patch-anatomy')) {
  customElements.define('pp-patch-anatomy', PerfectPatchesPatchAnatomy);
}
